import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { pageMotion } from '../../lib/motion';

/**
 * Page layout primitives — the information-architecture backbone. Every route
 * wraps its content in <PageContainer> and leads with a <PageHeader> so the user
 * always knows WHERE they are (breadcrumb + title) and WHAT they can do
 * (description + actions). Content is grouped into labelled <Section>s.
 */

export function PageContainer({
  children,
  className = '',
  size = 'default',
}: {
  children: ReactNode;
  className?: string;
  /** 'wide' for data-heavy consoles, 'narrow' for forms/settings. */
  size?: 'default' | 'wide' | 'narrow';
}) {
  const max = size === 'wide' ? 'max-w-[1600px]' : size === 'narrow' ? 'max-w-3xl' : 'max-w-[1320px]';
  return (
    // py-5, not py-6. Measured on the deployed build: the header block alone ran
    // 143px and the first control sat at y=247 of a 720px viewport — 34% of the
    // screen spent before any data. An operations console is judged on how much
    // it shows at a glance; the reclaimed rows go to content.
    <motion.div {...pageMotion} className={`mx-auto w-full ${max} px-5 py-5 sm:px-8 ${className}`}>
      {children}
    </motion.div>
  );
}

export interface Crumb {
  label: string;
  to?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  icon: Icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  icon?: LucideIcon;
}) {
  // A breadcrumb earns its place when it tells you something the rest of the
  // chrome does not. On a TOP-LEVEL page it does the opposite: "Forge ›
  // Scenarios" sits above a "Scenario Catalog" title while the sidebar already
  // has Scenarios lit — the same fact three times, costing a row on every page.
  // Shown from the second level down, where the parent is genuinely not
  // otherwise visible (a range detail, a lab inside a course).
  const showCrumbs = (breadcrumbs?.length ?? 0) > 2;
  return (
    <header className="mb-5">
      {showCrumbs && breadcrumbs && (
        <nav aria-label="Breadcrumb" className="mb-2.5 flex items-center gap-1.5 text-[12px] text-txt-muted">
          {breadcrumbs.map((c, i) => {
            const last = i === breadcrumbs.length - 1;
            return (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="size-3.5 text-txt-muted/60" />}
                {c.to && !last ? (
                  <Link to={c.to as never} className="transition-colors hover:text-txt-2 cursor-pointer">
                    {c.label}
                  </Link>
                ) : (
                  <span className={last ? 'font-medium text-txt-2' : ''}>{c.label}</span>
                )}
              </span>
            );
          })}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-neon-glow text-neon ring-1 ring-inset ring-neon/15">
              <Icon className="size-[18px]" />
            </span>
          )}
          <div className="min-w-0">
            <h1 className="font-display text-[22px] font-bold leading-tight tracking-tight text-txt-bright">
              {title}
            </h1>
            {description && (
              <p className="mt-1 max-w-2xl text-[13.5px] leading-relaxed text-txt-2">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export function Section({
  title,
  description,
  action,
  children,
  className = '',
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`mb-6 ${className}`}>
      {(title || action) && (
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            {title && (
              <h2 className="font-display text-[15px] font-bold tracking-tight text-txt-bright">{title}</h2>
            )}
            {description && <p className="mt-0.5 text-[12.5px] text-txt-2">{description}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
